import { ReactElement } from 'react';
import { ActionIcon, Card, Flex, Grid, Text, Title, Tooltip, rem } from '@mantine/core';
import { IconProps } from '@tabler/icons-react';

interface CardField {
  tooltip?: string;
  valueTooltip?: string;
  label: string;
  lineClamp?: number;
  value: string | number | boolean | ReactElement<any, any>;
  action?: () => {};
}

interface CardActionButton {
  loading?: boolean;
  tooltip?: string;
  Icon: ReactElement<any, any>;
  action: (cardItem?: any) => any;
  disabled: boolean;
  hidden?: boolean;
  testId?: string;
}

const CatalogCard = (prop: {
  fields: CardField[];
  cardActionButtons: CardActionButton[];
  cardTitle: string;
  TitleIcon?: React.ComponentType<IconProps>;
  cardTitleExtra?: React.ReactNode;
  cardItem?: any;
  cardTestId?: string;
  customColors?: {
    headerBackgroundColor?: string;
    headerTextColor?: string;
  };
}) => {
  const {
    fields,
    cardActionButtons = [],
    cardTitle,
    TitleIcon,
    cardTitleExtra,
    cardItem,
    cardTestId,
    customColors
  } = prop;
  const cardActionSpan = cardActionButtons.filter((btn) => !btn.hidden).length
    ? 12 / cardActionButtons.filter((btn) => !btn.hidden).length
    : 12;
  return (
    <Card data-testid={cardTestId}>
      <Card.Section
        inheritPadding
        withBorder
        py="xs"
        bg={customColors?.headerBackgroundColor}
        c={customColors?.headerTextColor}>
        <Grid align="center">
          <Grid.Col span="content" p={0} display={'flex'}>
            {TitleIcon && (
              <TitleIcon
                style={{ width: rem(20), height: rem(20) }}
                color={customColors?.headerTextColor}
              />
            )}
          </Grid.Col>
          <Grid.Col span="auto">
            <Title order={5} lineClamp={1} c={customColors?.headerTextColor}>
              {cardTitle}
            </Title>
          </Grid.Col>
          {cardTitleExtra && (
            <Grid.Col
              display={'flex'}
              style={{ alignItems: 'center', gap: rem(4), justifyContent: 'flex-end' }}
              span="content"
            >
              {cardTitleExtra}
            </Grid.Col>
          )}
        </Grid>
      </Card.Section>
      <Card.Section inheritPadding withBorder py="xs">
        <Grid gutter={0}>
          {fields.map((field, idx) => {
            return (
              <Grid.Col span={12} key={`${idx}-${field.label}`}>
                <Flex align="center">
                  {field.tooltip ? (
                    <Tooltip label={field.tooltip || field.label}>
                      <Text mr={5} fw={'bold'} lineClamp={2}>
                        {field.label}:
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text mr={5} fw={'bold'} lineClamp={2}>
                      {field.label}:
                    </Text>
                  )}
                  {field.valueTooltip ? (
                    <Tooltip withinPortal={false} label={field.valueTooltip || field.value}>
                      <Text lineClamp={field.lineClamp || 1}>{field.value}</Text>
                    </Tooltip>
                  ) : (
                    <Text lineClamp={field.lineClamp || 1}>{field.value}</Text>
                  )}
                </Flex>
              </Grid.Col>
            );
          })}
        </Grid>
      </Card.Section>
      <Card.Section>
        <Grid gutter={0}>
          {cardActionButtons.map((cardAction, idx) => {
            if (cardAction.hidden) {
              return <></>;
            }
            // JUST SHOW DIVIDER STYLE IF IS NOT THE LAST ACTION BUTTON (WE CAN ALSO DO THIS WITH CSS last-child)
            const actionDividerStyle =
              idx < cardActionButtons.length - 1 ? { borderRight: '1px solid white' } : {};
            return (
              <Grid.Col
                span={cardActionSpan}
                style={actionDividerStyle}
                key={`${idx}-${cardAction.tooltip}`}>
                {cardAction.tooltip ? (
                  <Tooltip label={cardAction.tooltip}>
                    <ActionIcon
                      data-testid={cardAction.testId}
                      loading={cardAction.loading}
                      disabled={cardAction.disabled}
                      radius={0}
                      size="xl"
                      w={'100%'}
                      onClick={() => cardAction.action(cardItem)}>
                      {cardAction.Icon}
                    </ActionIcon>
                  </Tooltip>
                ) : (
                  <ActionIcon
                    data-testid={cardAction.testId}
                    loading={cardAction.loading}
                    disabled={cardAction.disabled}
                    radius={0}
                    size="xl"
                    w={'100%'}
                    onClick={() => cardAction.action(cardItem)}>
                    {cardAction.Icon}
                  </ActionIcon>
                )}
              </Grid.Col>
            );
          })}
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default CatalogCard;
